import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Tooltip,
  Button,
  Grid,
  Typography,
  TextField,
  Paper,
  IconButton,
  ButtonGroup,
  MenuItem,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";

import { useDispatch, useSelector } from "react-redux";
import { signInGasBottle, signOutGasBottles } from "../../../actions/inventoryAction";
import Feedback from "../../../utils/Feedback";
import {
  getBarcodePolyfillClass,
  getGlobalBarcodeDetector,
  loadBarcodePolyfillRuntime,
  loadZxingRuntime,
  preloadBarcodeScannerRuntime,
} from "../../../utils/barcodeScannerRuntime";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 700,
    [theme.breakpoints.down("sm")]: {
      minWidth: 420,
    },
    [theme.breakpoints.down("xs")]: {
      minWidth: "100%",
    },
  },
  title: {
    fontSize: 13,
  },
  rowCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    border: "1px solid #e0e0e0",
  },
  btnGroup: {
    marginBottom: theme.spacing(2),
  },
  spacingTop: {
    marginTop: theme.spacing(1),
  },
  cameraInput: {
    display: "none",
  },
  decodeBackdrop: {
    zIndex: 1500,
    color: "#fff",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  decodeText: {
    fontWeight: 600,
  },
}));

const GAS_TYPES = ["Oxygen", "Acetylene", "Propane", "Argon", "Blueshield", "Other"];
const DESKTOP_DECODE_TIME_MS = 3500;
const MOBILE_DECODE_TIME_MS = 5500;
const MAX_CANVAS_DIMENSION = 1600;
const SUPPORTED_SCAN_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/bmp"];
const INIT_OUT_ROW = {
  gasType: "",
  serialNumber: "",
};

const CreateCompressedGasBottleSignModal = ({ openCreate, handleCloseCreate, onSubmitted }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [mode, setMode] = useState("Sign Out");
  const [jobNumber, setJobNumber] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [signOutRows, setSignOutRows] = useState([{ ...INIT_OUT_ROW }]);
  const [signInSerial, setSignInSerial] = useState("");
  const [scanTarget, setScanTarget] = useState(null);
  const [isDecoding, setIsDecoding] = useState(false);
  const cameraInputRef = useRef(null);

  const [feed, setFeed] = useState({
    open: false,
    message: "",
    success: false,
  });

  const { loading } = useSelector((state) => state.inventory);

  const resetState = () => {
    setMode("Sign Out");
    setJobNumber("");
    setQuantity("1");
    setSignOutRows([{ ...INIT_OUT_ROW }]);
    setSignInSerial("");
    setScanTarget(null);
  };

  const handleClose = () => {
    handleCloseCreate();
    resetState();
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setFeed((prev) => ({ ...prev, open: false }));
  };

  const resizeSignOutRows = (nextQuantity) => {
    setSignOutRows((prev) => {
      const rows = [...prev];
      if (rows.length < nextQuantity) {
        while (rows.length < nextQuantity) {
          rows.push({ ...INIT_OUT_ROW });
        }
      }
      if (rows.length > nextQuantity) {
        rows.length = nextQuantity;
      }
      return rows;
    });
  };

  const handleQuantityChange = (e) => {
    const rawValue = e.target.value;

    // Allow temporary empty input so users can replace the default value naturally.
    if (rawValue === "") {
      setQuantity("");
      return;
    }

    setQuantity(rawValue);
    const parsedValue = parseInt(rawValue, 10);
    if (!Number.isNaN(parsedValue) && parsedValue >= 1) {
      resizeSignOutRows(parsedValue);
    }
  };

  const handleQuantityBlur = () => {
    const parsedValue = parseInt(quantity, 10);
    const normalizedQuantity = !Number.isNaN(parsedValue) && parsedValue >= 1 ? parsedValue : 1;
    setQuantity(String(normalizedQuantity));
    resizeSignOutRows(normalizedQuantity);
  };

  const handleOutRowChange = (index, field, value) => {
    setSignOutRows((prev) => {
      const rows = [...prev];
      rows[index] = {
        ...rows[index],
        [field]: field === "serialNumber" ? value.toUpperCase() : value,
      };
      return rows;
    });
  };

  const handleOpenCamera = (target) => {
    if (isDecoding) {
      return;
    }

    setScanTarget(target);
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
      cameraInputRef.current.click();
    }
  };

  React.useEffect(() => {
    preloadBarcodeScannerRuntime();
  }, []);

  const getFirstBarcodeValue = async (detector, source) => {
    const matches = await detector.detect(source);
    if (!matches || matches.length === 0) {
      return null;
    }

    const firstReadable = matches.find((match) => match && match.rawValue && match.rawValue.trim());
    return firstReadable ? firstReadable.rawValue.trim().toUpperCase() : null;
  };

  const isMobileLikeDevice = () => {
    if (typeof navigator === "undefined") {
      return false;
    }

    const ua = navigator.userAgent || "";
    const mobileUa = /iPhone|iPad|iPod|Android|Mobile|Tablet/i.test(ua);
    const touchMac = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    return mobileUa || touchMac;
  };

  const createDecodeDeadline = () =>
    Date.now() + (isMobileLikeDevice() ? MOBILE_DECODE_TIME_MS : DESKTOP_DECODE_TIME_MS);

  const isDecodeTimeRemaining = (deadline) => Date.now() <= deadline;

  const drawCandidate = (ctx, image, crop, useEnhancedFilter) => {
    const sourceX = Math.floor(image.width * crop.x);
    const sourceY = Math.floor(image.height * crop.y);
    const sourceW = Math.max(1, Math.floor(image.width * crop.w));
    const sourceH = Math.max(1, Math.floor(image.height * crop.h));

    // Keep decode work bounded on very large camera images to avoid UI stalls.
    const maxSourceDimension = Math.max(sourceW, sourceH);
    const scaleToFit =
      maxSourceDimension > 0 ? Math.min(2, MAX_CANVAS_DIMENSION / maxSourceDimension) : 1;
    const drawScale = Math.max(1, scaleToFit);

    ctx.canvas.width = Math.max(1, Math.floor(sourceW * drawScale));
    ctx.canvas.height = Math.max(1, Math.floor(sourceH * drawScale));
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    if (useEnhancedFilter) {
      // Strong contrast and grayscale can improve 1D barcode detection on photos.
      ctx.filter = "grayscale(1) contrast(2.2) brightness(1.2)";
    }

    ctx.drawImage(image, sourceX, sourceY, sourceW, sourceH, 0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.filter = "none";
  };

  const detectFromRotations = async (detector, canvas, deadline) => {
    if (!isDecodeTimeRemaining(deadline)) {
      return null;
    }

    let value = await getFirstBarcodeValue(detector, canvas);
    if (value) {
      return value;
    }

    const rotations = [90, 180, 270];
    for (const degree of rotations) {
      if (!isDecodeTimeRemaining(deadline)) {
        return null;
      }

      const radians = (degree * Math.PI) / 180;
      const rotatedCanvas = document.createElement("canvas");
      const rotatedCtx = rotatedCanvas.getContext("2d");
      if (!rotatedCtx) {
        continue;
      }

      const isQuarterTurn = degree === 90 || degree === 270;
      rotatedCanvas.width = isQuarterTurn ? canvas.height : canvas.width;
      rotatedCanvas.height = isQuarterTurn ? canvas.width : canvas.height;

      rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
      rotatedCtx.rotate(radians);
      rotatedCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

      value = await getFirstBarcodeValue(detector, rotatedCanvas);
      if (value) {
        return value;
      }
    }

    return null;
  };

  const getNativeDetector = async () => {
    const NativeBarcodeDetector = getGlobalBarcodeDetector();
    if (!NativeBarcodeDetector) {
      return null;
    }

    let formats = [];
    try {
      if (typeof window.BarcodeDetector.getSupportedFormats === "function") {
        formats = await window.BarcodeDetector.getSupportedFormats();
      }
    } catch (err) {
      formats = [];
    }

    try {
      return formats.length ? new NativeBarcodeDetector({ formats }) : new NativeBarcodeDetector();
    } catch (err) {
      return null;
    }
  };

  const getPolyfillDetector = async () => {
    await loadBarcodePolyfillRuntime();

    const polyfillClass = getBarcodePolyfillClass();
    if (!polyfillClass) {
      throw new Error("Barcode polyfill failed to initialize");
    }

    let formats = [];
    if (typeof polyfillClass.getSupportedFormats === "function") {
      formats = await polyfillClass.getSupportedFormats();
    }

    return formats.length ? new polyfillClass({ formats }) : new polyfillClass();
  };

  const decodeCanvasWithZxing = (zxing, reader, canvas) => {
    const ctx = canvas.getContext("2d");
    if (!ctx || canvas.width === 0 || canvas.height === 0) {
      return null;
    }

    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const luminanceSource = new zxing.RGBLuminanceSource(imageData.data, canvas.width, canvas.height);
      const binaryBitmap = new zxing.BinaryBitmap(new zxing.HybridBinarizer(luminanceSource));
      const result = reader.decode(binaryBitmap);
      const text = result && typeof result.getText === "function" ? result.getText() : null;
      return text ? text.trim().toUpperCase() : null;
    } catch (err) {
      return null;
    } finally {
      if (typeof reader.reset === "function") {
        reader.reset();
      }
    }
  };

  const decodeCanvasWithZxingRotations = (zxing, reader, canvas, deadline) => {
    if (!isDecodeTimeRemaining(deadline)) {
      return null;
    }

    let value = decodeCanvasWithZxing(zxing, reader, canvas);
    if (value) {
      return value;
    }

    const rotations = [90, 180, 270];
    for (const degree of rotations) {
      if (!isDecodeTimeRemaining(deadline)) {
        return null;
      }

      const radians = (degree * Math.PI) / 180;
      const rotatedCanvas = document.createElement("canvas");
      const rotatedCtx = rotatedCanvas.getContext("2d");
      if (!rotatedCtx) {
        continue;
      }

      const isQuarterTurn = degree === 90 || degree === 270;
      rotatedCanvas.width = isQuarterTurn ? canvas.height : canvas.width;
      rotatedCanvas.height = isQuarterTurn ? canvas.width : canvas.height;

      rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
      rotatedCtx.rotate(radians);
      rotatedCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

      value = decodeCanvasWithZxing(zxing, reader, rotatedCanvas);
      if (value) {
        return value;
      }
    }

    return null;
  };

  const detectWithZxing = async (image, deadline) => {
    if (!isDecodeTimeRemaining(deadline)) {
      return null;
    }

    await loadZxingRuntime();

    if (!isDecodeTimeRemaining(deadline)) {
      return null;
    }

    const zxing = window.ZXing;
    if (!zxing || !zxing.MultiFormatReader || !zxing.RGBLuminanceSource || !zxing.BinaryBitmap || !zxing.HybridBinarizer) {
      throw new Error("ZXing runtime unavailable");
    }

    const reader = new zxing.MultiFormatReader();
    if (zxing.DecodeHintType && zxing.BarcodeFormat) {
      const hints = new Map();
      hints.set(zxing.DecodeHintType.POSSIBLE_FORMATS, [
        zxing.BarcodeFormat.CODE_128,
        zxing.BarcodeFormat.CODE_39,
        zxing.BarcodeFormat.CODE_39_MOD_43,
        zxing.BarcodeFormat.CODE_93,
        zxing.BarcodeFormat.CODABAR,
        zxing.BarcodeFormat.ITF,
        zxing.BarcodeFormat.EAN_8,
        zxing.BarcodeFormat.EAN_13,
        zxing.BarcodeFormat.UPC_A,
        zxing.BarcodeFormat.UPC_E,
        zxing.BarcodeFormat.RSS_14,
        zxing.BarcodeFormat.RSS_EXPANDED,
        zxing.BarcodeFormat.DATA_MATRIX,
        zxing.BarcodeFormat.AZTEC,
        zxing.BarcodeFormat.PDF_417,
      ]);

      // Improve decode success for lower quality or older labels.
      if (zxing.DecodeHintType.TRY_HARDER) {
        hints.set(zxing.DecodeHintType.TRY_HARDER, true);
      }

      if (typeof reader.setHints === "function") {
        reader.setHints(hints);
      }
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }

    const candidateCrops = [
      { x: 0, y: 0, w: 1, h: 1 },
      { x: 0.1, y: 0.2, w: 0.8, h: 0.6 },
      { x: 0.2, y: 0.35, w: 0.6, h: 0.35 },
      { x: 0.25, y: 0.45, w: 0.5, h: 0.25 },
    ];

    for (const crop of candidateCrops) {
      if (!isDecodeTimeRemaining(deadline)) {
        return null;
      }

      drawCandidate(ctx, image, crop, false);
      let value = decodeCanvasWithZxingRotations(zxing, reader, canvas, deadline);
      if (value) {
        return value;
      }

      if (!isDecodeTimeRemaining(deadline)) {
        return null;
      }

      drawCandidate(ctx, image, crop, true);
      value = decodeCanvasWithZxingRotations(zxing, reader, canvas, deadline);
      if (value) {
        return value;
      }
    }

    return null;
  };

  const createImageSource = async (file) => {
    if (typeof createImageBitmap === "function") {
      try {
        const bitmap = await createImageBitmap(file);
        return {
          image: bitmap,
          cleanup: () => {
            if (typeof bitmap.close === "function") {
              bitmap.close();
            }
          },
        };
      } catch (err) {
        // Fallback to HTMLImageElement for browsers with limited createImageBitmap support.
      }
    }

    const objectUrl = URL.createObjectURL(file);
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load image for decoding"));
      img.src = objectUrl;
    });

    return {
      image,
      cleanup: () => URL.revokeObjectURL(objectUrl),
    };
  };

  const detectWithDetector = async (detector, image, deadline) => {
    if (!isDecodeTimeRemaining(deadline)) {
      return null;
    }

    let value = await getFirstBarcodeValue(detector, image);

    if (value) {
      return value;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }

    const candidateCrops = [
      { x: 0, y: 0, w: 1, h: 1 },
      { x: 0.1, y: 0.2, w: 0.8, h: 0.6 },
      { x: 0.2, y: 0.35, w: 0.6, h: 0.35 },
      { x: 0.25, y: 0.45, w: 0.5, h: 0.25 },
    ];

    for (const crop of candidateCrops) {
      if (!isDecodeTimeRemaining(deadline)) {
        return null;
      }

      drawCandidate(ctx, image, crop, false);
      value = await detectFromRotations(detector, canvas, deadline);
      if (value) {
        return value;
      }

      if (!isDecodeTimeRemaining(deadline)) {
        return null;
      }

      drawCandidate(ctx, image, crop, true);
      value = await detectFromRotations(detector, canvas, deadline);
      if (value) {
        return value;
      }
    }

    return null;
  };

  const handleBarcodeImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    if (file.type && !SUPPORTED_SCAN_MIME_TYPES.includes(file.type)) {
      setFeed({
        open: true,
        message:
          "This image format is not supported for barcode scan on this device. Use JPEG/PNG or enter serial manually.",
        success: false,
      });
      return;
    }

    setIsDecoding(true);

    let imageSource;
    try {
      const decodeDeadline = createDecodeDeadline();
      imageSource = await createImageSource(file);
      const image = imageSource.image;

      let value = null;

      const nativeDetector = await getNativeDetector();
      if (nativeDetector && isDecodeTimeRemaining(decodeDeadline)) {
        value = await detectWithDetector(nativeDetector, image, decodeDeadline);
      }

      if (!value && isDecodeTimeRemaining(decodeDeadline)) {
        try {
          const polyfillDetector = await getPolyfillDetector();
          value = await detectWithDetector(polyfillDetector, image, decodeDeadline);
        } catch (polyfillError) {
          // Continue to ZXing fallback when polyfill scripts are blocked or unavailable.
        }
      }

      if (!value && isDecodeTimeRemaining(decodeDeadline)) {
        try {
          value = await detectWithZxing(image, decodeDeadline);
        } catch (zxingError) {
          // Surface generic failure message below if all decoding strategies fail.
        }
      }

      if (!value) {
        setFeed({
          open: true,
          message:
            "Barcode could not be decoded on this browser. Please try Safari/Chrome update or enter serial manually.",
          success: false,
        });
        return;
      }

      if (!scanTarget) {
        return;
      }

      if (scanTarget.type === "out") {
        handleOutRowChange(scanTarget.index, "serialNumber", value);
      } else {
        setSignInSerial(value);
      }
    } catch (scanError) {
      setFeed({
        open: true,
        message: "Barcode decoding failed on this device/browser. Please enter serial manually.",
        success: false,
      });
    } finally {
      if (imageSource && typeof imageSource.cleanup === "function") {
        imageSource.cleanup();
      }
      setIsDecoding(false);
    }
  };

  const handleSignOutSubmit = async (e) => {
    e.preventDefault();

    const hasInvalidRow = signOutRows.some((row) => !row.gasType.trim() || !row.serialNumber.trim());
    if (hasInvalidRow) {
      setFeed({
        open: true,
        message: "Each bottle row requires Gas Type and Serial Number.",
        success: false,
      });
      return;
    }

    const serials = signOutRows.map((row) => row.serialNumber.trim().toUpperCase());
    if (new Set(serials).size !== serials.length) {
      setFeed({
        open: true,
        message: "Duplicate serial numbers detected in this sign out submission.",
        success: false,
      });
      return;
    }

    try {
      await dispatch(
        signOutGasBottles({
          jobNumber: jobNumber.trim() || "N/A",
          bottles: signOutRows.map((row) => ({
            gasType: row.gasType.trim(),
            serialNumber: row.serialNumber.trim().toUpperCase(),
          })),
        })
      );

      if (onSubmitted) {
        onSubmitted();
      }
      setFeed({
        open: true,
        message: `${signOutRows.length} compressed gas bottle(s) signed out successfully.`,
        success: true,
      });
      handleClose();
    } catch (err) {
      setFeed({
        open: true,
        message: err.message || "Failed to sign out compressed gas bottle(s).",
        success: false,
      });
    }
  };

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    if (!signInSerial.trim()) {
      setFeed({
        open: true,
        message: "Serial Number is required to sign in a bottle.",
        success: false,
      });
      return;
    }

    try {
      await dispatch(
        signInGasBottle({
          serialNumber: signInSerial.trim().toUpperCase(),
        })
      );

      if (onSubmitted) {
        onSubmitted();
      }
      setFeed({
        open: true,
        message: "Compressed gas bottle signed in successfully.",
        success: true,
      });
      handleClose();
    } catch (err) {
      setFeed({
        open: true,
        message: err.message || "Failed to sign in compressed gas bottle.",
        success: false,
      });
    }
  };

  const handleCloseFeed = () => {
    setFeed((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/bmp"
        capture="environment"
        onChange={handleBarcodeImage}
        className={classes.cameraInput}
      />

      <Feedback
        handleCloseFeed={handleCloseFeed}
        open={feed.open}
        severity={feed.success ? "success" : "error"}
        message={feed.message}
      />

      <Backdrop className={classes.decodeBackdrop} open={isDecoding}>
        <CircularProgress color="inherit" />
        <Typography variant="body2" className={classes.decodeText}>
          Decoding barcode...
        </Typography>
      </Backdrop>

      <Dialog open={openCreate} onClose={handleClose} aria-labelledby="form-dialog-title" fullWidth maxWidth="md">
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="overline" className={classes.title}>
                Compressed Gas Bottle Sign In/Out
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip arrow title="Close">
                <IconButton onClick={handleClose} size="large">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />

        <DialogContent className={classes.root}>
          <ButtonGroup variant="outlined" color="primary" className={classes.btnGroup}>
            <Button
              variant={mode === "Sign Out" ? "contained" : "outlined"}
              onClick={() => handleModeChange("Sign Out")}
            >
              Sign Out
            </Button>
            <Button
              variant={mode === "Sign In" ? "contained" : "outlined"}
              onClick={() => handleModeChange("Sign In")}
            >
              Sign In
            </Button>
          </ButtonGroup>

          {mode === "Sign Out" ? (
            <form onSubmit={handleSignOutSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    label="Quantity"
                    inputProps={{ min: 1, inputMode: "numeric" }}
                    value={quantity}
                    onChange={handleQuantityChange}
                    onBlur={handleQuantityBlur}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Job Number"
                    value={jobNumber}
                    onChange={(e) => setJobNumber(e.target.value.toUpperCase())}
                    helperText='Use "N/A" for return to stock'
                    inputProps={{ style: { textTransform: "uppercase" } }}
                  />
                </Grid>
              </Grid>

              {signOutRows.map((row, index) => (
                <Paper key={`bottle-row-${index}`} className={classes.rowCard} variant="outlined">
                  <Typography variant="body2" gutterBottom>
                    Bottle {index + 1}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Gas Type"
                        select
                        value={row.gasType}
                        onChange={(e) => handleOutRowChange(index, "gasType", e.target.value)}
                        required
                      >
                        {GAS_TYPES.map((gasType) => (
                          <MenuItem key={gasType} value={gasType}>
                            {gasType}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Serial Number"
                        value={row.serialNumber}
                        onChange={(e) => handleOutRowChange(index, "serialNumber", e.target.value)}
                        required
                        inputProps={{ style: { textTransform: "uppercase" } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Tooltip title="Scan barcode with phone camera (if supported)">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenCamera({ type: "out", index })}
                          disabled={isDecoding}
                        >
                          <PhotoCameraIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              <Grid container className={classes.spacingTop}>
                <Grid item>
                  <Button type="submit" color="primary" variant="contained" disabled={loading}>
                    {loading ? "Processing..." : "Submit Sign Out"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          ) : (
            <form onSubmit={handleSignInSubmit}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Serial Number"
                    value={signInSerial}
                    onChange={(e) => setSignInSerial(e.target.value.toUpperCase())}
                    required
                    helperText="Enter the bottle serial number to return it"
                    inputProps={{ style: { textTransform: "uppercase" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Tooltip title="Scan barcode with phone camera (if supported)">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenCamera({ type: "in" })}
                      disabled={isDecoding}
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button type="submit" color="primary" variant="contained" fullWidth disabled={loading}>
                    {loading ? "Processing..." : "Submit Sign In"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateCompressedGasBottleSignModal;
